<script setup lang="ts">
/**
 * AiOrbIcon — the single source of truth for the AI orb, animated or static.
 *
 * Static (the default) is the identity mark: same gradient, ring geometry, core and glow, motion
 * removed. Animation is reserved for the small set of placements where motion means "working" —
 * callers must opt in explicitly with `animated`. This is deliberate: defaulting to animated would
 * let "one continuously-spinning orb per row" regress with the next careless call site. There
 * should only ever be one animation version of it (ratified on #1222).
 */
const props = withDefaults(defineProps<{ size?: number; animated?: boolean }>(), {
  size: 16,
  animated: false,
})
</script>

<template>
  <span
    class="ai-orb-icon inline-flex items-center justify-center relative"
    :class="{ 'ai-orb-icon--animated': props.animated }"
    :style="{ width: `${props.size}px`, height: `${props.size}px` }"
  >
    <!-- Green body -->
    <span class="ai-orb-icon__bg" />
    <!-- White orbital rings (the spinner) -->
    <span
      class="ai-orb-icon__ring ai-orb-icon__ring--outer"
      :style="{
        width: `${props.size}px`,
        height: `${props.size}px`,
        borderWidth: `${Math.max(1, props.size / 11)}px`,
      }"
    />
    <span
      class="ai-orb-icon__ring ai-orb-icon__ring--inner"
      :style="{
        width: `${props.size * 0.72}px`,
        height: `${props.size * 0.72}px`,
        borderWidth: `${Math.max(1, props.size / 11)}px`,
      }"
    />
    <!-- White core -->
    <span
      class="ai-orb-icon__core"
      :style="{
        width: `${props.size * 0.3}px`,
        height: `${props.size * 0.3}px`,
      }"
    />
  </span>
</template>

<style scoped>
.ai-orb-icon {
  /* Theme green (SiteCompass brand), not Tailwind emerald */
  --orb-green-light: theme('colors.brand.400');
  --orb-green-dark: theme('colors.brand.600');
  flex-shrink: 0;
}

.ai-orb-icon__bg {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle at 35% 30%,
    var(--orb-green-light) 0%,
    var(--orb-green-dark) 100%
  );
  box-shadow: 0 0 6px 1px color-mix(in srgb, var(--orb-green-light) 45%, transparent);
}

.ai-orb-icon__ring {
  position: absolute;
  border-radius: 50%;
  border-style: solid;
  border-color: transparent;
  pointer-events: none;
}

.ai-orb-icon__ring--outer {
  border-top-color: #ffffff;
  border-right-color: #ffffff;
  opacity: 0.95;
}

.ai-orb-icon__ring--inner {
  border-bottom-color: #ffffff;
  border-left-color: rgba(255, 255, 255, 0.85);
  opacity: 0.75;
}

.ai-orb-icon__core {
  position: absolute;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 4px 1px rgba(255, 255, 255, 0.6);
}

/* Motion is opt-in via the `animated` prop, and further gated by prefers-reduced-motion —
   the static variant (default) never animates regardless of either. */
.ai-orb-icon--animated .ai-orb-icon__ring--outer {
  animation: orb-icon-spin 3s linear infinite;
}

.ai-orb-icon--animated .ai-orb-icon__ring--inner {
  animation: orb-icon-spin 2s linear infinite reverse;
}

.ai-orb-icon--animated .ai-orb-icon__core {
  animation: orb-icon-pulse 2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .ai-orb-icon--animated .ai-orb-icon__ring--outer,
  .ai-orb-icon--animated .ai-orb-icon__ring--inner,
  .ai-orb-icon--animated .ai-orb-icon__core {
    animation: none;
  }
}

@keyframes orb-icon-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes orb-icon-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}
</style>
